// My Desk canvas grid math — Request Ledger N-090: "Cards can be set in
// different sizes ... like a real canvas." Pure functions only (no preact,
// no DOM) so they are exercised directly by client/tests/grid-pack.test.ts
// without a render pass — the arrange-mode drag/drop and Add-panel flow in
// DeskGrid.tsx are thin wrappers around these.
//
// The canvas is a 12-column grid with a fixed row unit (~120px, set in
// global.css). Every panel occupies a rectangle: `col`/`row` are 0-based
// grid-cell coordinates, `w` is a column span, `h` is a row-unit span.

export const GRID_COLUMNS = 12;

export interface GridSpan {
  col: number;
  row: number;
  w: number;
  h: number;
}

/** Two spans occupy the same cell(s). */
export function overlaps(a: GridSpan, b: GridSpan): boolean {
  return (
    a.col < b.col + b.w && b.col < a.col + a.w
    && a.row < b.row + b.h && b.row < a.row + a.h
  );
}

/**
 * The first open col/row (reading top-to-bottom, left-to-right) that fits a
 * `w`×`h` box without overlapping anything in `existing` — what "Add panel"
 * drops a new panel at, and what a fresh person's default layout is packed
 * with.
 */
export function firstFreeSpot(existing: GridSpan[], w: number, h: number): { col: number; row: number } {
  const maxRow = existing.reduce((m, p) => Math.max(m, p.row + p.h), 0);
  // +1 extra row of headroom: a box taller than every existing panel still
  // needs somewhere below the lowest one to land.
  for (let row = 0; row <= maxRow + h; row += 1) {
    for (let col = 0; col <= GRID_COLUMNS - w; col += 1) {
      const candidate: GridSpan = {
        col, row, w, h,
      };
      if (!existing.some((p) => overlaps(candidate, p))) return { col, row };
    }
  }
  // Unreachable in practice (the loop above always finds col=0 at some row),
  // but a defined fallback beats an undefined one.
  return { col: 0, row: maxRow };
}

/**
 * Push down whatever now overlaps, after a resize or a drag-drop moved one
 * panel. `panels` is processed in the given order — put the just-moved/
 * resized panel FIRST so everything else yields to it, exactly like a real
 * desk: the thing you just placed keeps its spot, and whatever was already
 * there moves. Each later panel is dropped to just below the lowest
 * already-settled panel it overlaps, then re-checked against everything
 * settled so far — so a chain of pushes never re-overlaps something it
 * already cleared.
 *
 * Deterministic and terminating: a panel's row only ever increases, and it
 * increases by at least one settled panel's height each pass, so the loop
 * below cannot run longer than `panels.length` passes.
 */
export function resolveOverlaps<T extends GridSpan>(panels: T[]): T[] {
  const settled: T[] = [];
  for (const panel of panels) {
    let next = { ...panel };
    // Re-check after every push: settling this panel lower can create a NEW
    // overlap with a panel that was previously clear of it.
    for (let guard = 0; guard < panels.length + 1; guard += 1) {
      const hit = settled.find((s) => overlaps(next, s));
      if (!hit) break;
      next = { ...next, row: hit.row + hit.h };
    }
    settled.push(next);
  }
  return settled;
}

/**
 * Close the holes a removed panel leaves (owner, 2026-10-12: Tomer's own
 * Desk carried to everyone, minus what their role cannot see): reading
 * top-to-bottom, left-to-right, each panel moves UP as far as it can without
 * overlapping one already placed — never sideways, never down unless it
 * overlapped to begin with. Input order kept. Server twin: src/desk.js
 * floatUp (client/tests/desk-templates.test.tsx holds them to one answer).
 */
export function floatUp<T extends GridSpan>(panels: readonly T[]): T[] {
  const order = panels.map((_, i) => i)
    .sort((a, b) => (panels[a].row - panels[b].row) || (panels[a].col - panels[b].col) || (a - b));
  const out = panels.map((p) => ({ ...p }));
  const placed: T[] = [];
  for (const i of order) {
    const p = out[i];
    const hits = (row: number) => placed.some((s) => overlaps({ ...p, row }, s));
    let { row } = p;
    if (hits(row)) {
      while (hits(row)) row += 1;
    } else {
      while (row > 0 && !hits(row - 1)) row -= 1;
    }
    p.row = row;
    placed.push(p);
  }
  return out;
}

/**
 * Pointer-drag delta (pixels) to a grid-cell delta, given the column step
 * (column width + gap) and row step (row-unit height + gap) in pixels.
 * Split out from the DOM handler so the rounding rule — snap to the
 * NEAREST cell, not the floor — is unit-tested without a real layout.
 */
export function pxDeltaToCellDelta(
  dxPx: number,
  dyPx: number,
  colStepPx: number,
  rowStepPx: number,
): { dCol: number; dRow: number } {
  const dCol = colStepPx > 0 ? Math.round(dxPx / colStepPx) : 0;
  const dRow = rowStepPx > 0 ? Math.round(dyPx / rowStepPx) : 0;
  return { dCol, dRow };
}

/** Clamp a proposed col so the panel of width `w` stays on the grid. */
export function clampCol(col: number, w: number): number {
  return Math.max(0, Math.min(col, GRID_COLUMNS - w));
}

/** Clamp a proposed row to non-negative — there is no ceiling on the canvas. */
export function clampRow(row: number): number {
  return Math.max(0, row);
}

// --- N-090b (owner, 2026-09-08): "make boxes bigger horizontally and
// vertically" by dragging their edges, instead of picking a size from a
// segmented control. The six width fractions and four height units are the
// same two closed sets the segmented control used to expose — DeskGrid.tsx
// no longer renders them as buttons, but the server (src/desk.js WIDTHS /
// HEIGHTS) still only accepts these exact values, so every drag still has to
// land on one of them. Kept here, not re-derived from panelCatalog.ts's old
// SizeLabel maps (deleted with the buttons) — this module stays free of any
// import from a file that pulls in preact/components (see the module
// comment above), and a plain number array is all a resize-snap needs.
export const SNAP_WIDTHS: readonly number[] = [3, 4, 6, 8, 9, 12];
export const SNAP_HEIGHTS: readonly number[] = [1, 2, 3, 4];

/** The id generator every "new panel" needs (Add panel, and nothing else) —
 * moved here from DeskGrid.tsx so the newly-split DeskAddMenu.tsx can share
 * it without either file importing the other. Not cryptographically unique,
 * only unique enough to key one person's own layout (never compared across
 * logins). */
export function newPanelId(): string {
  return `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/** The closest member of `set` to `value`. Ties favour the EARLIER (so, for
 * an ascending set, the smaller) candidate — an arbitrary but fixed and
 * documented tie-break, same spirit as panelCatalog's old heightLabel one. */
export function nearestInSet(value: number, set: readonly number[]): number {
  let best = set[0];
  let bestDist = Math.abs(value - best);
  for (const candidate of set) {
    const dist = Math.abs(value - candidate);
    if (dist < bestDist) {
      best = candidate;
      bestDist = dist;
    }
  }
  return best;
}

/** LIVE width while a resize is in progress — every whole column the pointer
 * has crossed, clamped to the grid, but NOT yet snapped to a size fraction.
 * Snapping happens once, on release (`snapWidth`) — while dragging, the box
 * should visibly grow/shrink one column at a time, not jump between the six
 * fixed fractions. */
export function clampWidthValue(w: number, col: number): number {
  return Math.max(1, Math.min(w, GRID_COLUMNS - col));
}

/** LIVE height while a resize is in progress — see `clampWidthValue`. No
 * upper bound is applied here (the canvas has no row ceiling); `snapHeight`
 * is what actually caps it to the server's HEIGHTS set on release. */
export function clampHeightValue(h: number): number {
  return Math.max(1, h);
}

/** Snap a released width to the nearest value the server will accept,
 * without ever landing somewhere the panel would spill off the 12-column
 * grid. Every panel's CURRENT width is already one of `SNAP_WIDTHS` (the
 * catalog's default sizes, and every past resize, only ever produce one of
 * these six values), and `col` was already clamped so `col + w <= 12` — so
 * `GRID_COLUMNS - col` is always at least the current width, and the filter
 * below can never come up empty. The outer `Math.min` is a defensive second
 * clamp, not load-bearing given that invariant. */
export function snapWidth(rawW: number, col: number): number {
  const maxW = GRID_COLUMNS - col;
  const candidates = SNAP_WIDTHS.filter((w) => w <= maxW);
  const pool = candidates.length ? candidates : SNAP_WIDTHS;
  return Math.min(nearestInSet(rawW, pool), maxW);
}

/** Snap a released height to the nearest of the server's four row-unit
 * values. */
export function snapHeight(rawH: number): number {
  return nearestInSet(rawH, SNAP_HEIGHTS);
}

/** Keyboard fallback (shift+arrow): move to the NEXT or PREVIOUS entry in
 * the six-value width set, one keypress = one step — not a raw column at a
 * time, so a keyboard user lands on exactly the same six sizes a mouse-drag
 * snaps to. `dir` is +1 (grow) or -1 (shrink). Already-valid widths (see
 * `snapWidth`'s comment) always have an exact index; -1 is a defined
 * fallback for a value that somehow is not one of the six, not a case that
 * arises in practice. */
export function stepWidth(w: number, dir: 1 | -1): number {
  const widths = SNAP_WIDTHS as number[];
  const idx = widths.indexOf(w);
  const base = idx === -1 ? 0 : idx;
  const next = Math.max(0, Math.min(widths.length - 1, base + dir));
  return widths[next];
}

/** Keyboard fallback (shift+arrow) for height — same idea as `stepWidth`,
 * over the four-value height set. */
export function stepHeight(h: number, dir: 1 | -1): number {
  const heights = SNAP_HEIGHTS as number[];
  const idx = heights.indexOf(h);
  const base = idx === -1 ? 0 : idx;
  const next = Math.max(0, Math.min(heights.length - 1, base + dir));
  return heights[next];
}
